import { Link, Outlet, createFileRoute, redirect } from "@tanstack/react-router";
import {
  Bell,
  Building2,
  Clock,
  Mail,
  ShieldCheck,
  Tag,
  User as UserIcon,
  UserCircle2,
  Users,
  Workflow,
  type LucideIcon,
} from "lucide-react";

import { cn } from "@/lib/cn";
import { isAdminOrAbove, isModeratorOrAbove, isSuperadmin } from "@/lib/roles";
import { useAuth } from "@/stores/auth";

type SettingsRoute =
  | "/settings/mailboxes"
  | "/settings/users"
  | "/settings/groups"
  | "/settings/mailbox-access"
  | "/settings/tags"
  | "/settings/sla-policies"
  | "/settings/assignment-rules"
  | "/settings/delegations"
  | "/settings/organizations"
  | "/settings/profile"
  | "/settings/notifications";

interface SettingsItem {
  to: SettingsRoute;
  label: string;
  icon: LucideIcon;
}

interface SettingsSection {
  label: string;
  items: SettingsItem[];
  visible: (role: string | undefined) => boolean;
}

const SECTIONS: SettingsSection[] = [
  {
    label: "Account",
    visible: () => true,
    items: [
      { to: "/settings/profile", label: "Profile", icon: UserIcon },
      { to: "/settings/notifications", label: "Notifications", icon: Bell },
    ],
  },
  {
    label: "Channels",
    visible: (role) => isModeratorOrAbove(role),
    items: [
      { to: "/settings/mailboxes", label: "Mailboxes", icon: Mail },
      { to: "/settings/mailbox-access", label: "Mailbox access", icon: ShieldCheck },
    ],
  },
  {
    label: "Team",
    visible: (role) => isModeratorOrAbove(role),
    items: [
      { to: "/settings/users", label: "Users", icon: UserCircle2 },
    ],
  },
  {
    label: "Team",
    visible: (role) => isAdminOrAbove(role),
    items: [
      { to: "/settings/groups", label: "Groups", icon: Users },
    ],
  },
  {
    label: "Workflows",
    visible: (role) => isAdminOrAbove(role),
    items: [
      { to: "/settings/tags", label: "Tags", icon: Tag },
      { to: "/settings/sla-policies", label: "SLA policies", icon: Clock },
      { to: "/settings/assignment-rules", label: "Assignment rules", icon: Workflow },
    ],
  },
  {
    label: "Platform",
    visible: (role) => isSuperadmin(role),
    items: [
      { to: "/settings/organizations", label: "Organizations", icon: Building2 },
      { to: "/settings/delegations", label: "Delegations", icon: ShieldCheck },
    ],
  },
];

function SettingsLayout() {
  const role = useAuth((s) => s.user?.role);

  // Merge sections by label so moderator + admin "Team" rows render under one
  // header (Users above Groups). Iteration order keeps the original ordering
  // — the first occurrence of a label sets the position.
  const visible = SECTIONS.filter((s) => s.visible(role));
  const merged: SettingsSection[] = [];
  for (const section of visible) {
    const existing = merged.find((m) => m.label === section.label);
    if (existing) {
      existing.items.push(...section.items);
    } else {
      merged.push({ ...section, items: [...section.items] });
    }
  }

  return (
    <div className="flex h-full flex-col md:flex-row">
      <aside className="shrink-0 border-b border-border bg-surface md:w-64 md:border-b-0 md:border-r md:overflow-y-auto md:scrollbar-thin">
        <div className="px-4 py-4 md:px-3 md:py-4">
          <h2 className="px-2 text-sm font-medium tracking-tight text-text">Settings</h2>
        </div>
        <nav className="flex flex-row gap-1 overflow-x-auto px-2 pb-3 md:flex-col md:gap-0.5 md:overflow-visible md:px-2 md:pb-4">
          {merged.map((section, sIdx) => (
            <SettingsSectionGroup key={`${section.label}-${sIdx}`} section={section} />
          ))}
        </nav>
      </aside>
      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
}

function SettingsSectionGroup({ section }: { section: SettingsSection }) {
  return (
    <div className="contents md:block">
      <div className="hidden px-2 pt-3 pb-1 text-[10px] font-medium uppercase tracking-wider text-text-faint md:block">
        {section.label}
      </div>
      {section.items.map((item) => (
        <SettingsNavLink key={item.to} item={item} />
      ))}
    </div>
  );
}

function SettingsNavLink({ item }: { item: SettingsItem }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      className={cn(
        "shrink-0 inline-flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm text-text-muted transition-colors",
        "hover:bg-surface-subtle hover:text-text",
        "[&.active]:bg-accent-subtle [&.active]:text-accent-strong [&.active]:font-medium",
        "md:flex",
      )}
      activeOptions={{ exact: true }}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span className="whitespace-nowrap">{item.label}</span>
    </Link>
  );
}

export const Route = createFileRoute("/_auth/settings")({
  beforeLoad: ({ location }) => {
    const role = useAuth.getState().user?.role;
    const moderatorOrAbove = isModeratorOrAbove(role);
    const adminOrAbove = isAdminOrAbove(role);
    const personalRoutes = ["/settings/profile", "/settings/notifications"];
    const isPersonal = personalRoutes.some((p) => location.pathname.startsWith(p));
    if (!moderatorOrAbove && !isPersonal) {
      throw redirect({ to: "/settings/notifications" });
    }
    if (location.pathname === "/settings" || location.pathname === "/settings/") {
      if (adminOrAbove) {
        throw redirect({ to: "/settings/mailboxes" });
      }
      if (moderatorOrAbove) {
        throw redirect({ to: "/settings/mailboxes" });
      }
      throw redirect({ to: "/settings/profile" });
    }
  },
  component: SettingsLayout,
});
